import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import InvoiceDetailsBillingAddress, {
    InvoiceDetailsBillingAddressStyles,
} from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsBillingAddress";
import InvoiceDetailsShippingAddress, {
    InvoiceDetailsShippingAddressStyles,
} from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsShippingAddress";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import OrderDetailPageTypeLink from "@tKgpco/Overrides/Components/OrderDetailPageTypeLink";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import Select, { SelectProps } from "@insite/mobius/Select";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import updateDiscrepancyStatus from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/UpdateDiscrepancyStatus";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import loadDiscrepancyDetailsCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyDetailsCollection";
import loadDiscrepancyCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyCollection";

const mapStateToProps = (state: CustomApplicationState) => ({
    language: state.context.session.language,
    discrepancyDetails: state.KGPState.DiscrepancyStore.discrepancyDetailsCollection?.discrepancyDetails,
    parameters: state.KGPState.DiscrepancyStore.discrepancyCollectionParams,
    featureWidgets: state.context.session.properties.featureWidgets,
    isSecurityRolesAssigned: state.context.session?.properties?.isSecurityRolesAssigned?.toLowerCase() === "true",
});

const mapDispatchToProps = {
    updateDiscrepancyStatus,
    loadDiscrepancyDetailsCollection,
    loadDiscrepancyCollection,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceDetailsInformationStyles {
    invoiceInformationGridContainer?: GridContainerProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    informationGridItem?: GridItemProps;
    invoiceInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    shippingMethodGridItem?: GridItemProps;
    invoiceDateGridItem?: GridItemProps;
    termsGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    invoiceNotesGridItem?: GridItemProps;
    billingAddressStyle?: InvoiceDetailsBillingAddressStyles;
    shippingAddressStyle?: InvoiceDetailsShippingAddressStyles;
    SalesOrderGridItem?: GridItemProps;
    SalesOrderText?: InjectableCss;
    SalesOrderTitle?: InjectableCss;
}

export const informationStyles: InvoiceDetailsInformationStyles = {
    invoiceInformationGridContainer: {
        css: css`
            padding: 5px;
        `,
    },
    // headingAndTextStyles: {
    //     heading: {
    //         variant: "h6",
    //         as: "h2",
    //         css: css`
    //             @media print {
    //                 font-size: 12px;
    //             }
    //             margin-bottom: 5px;
    //         `,
    //     },
    // },
    headingAndTextStyles: {
        heading: {
            variant: "h3",
            as: "h3",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
        text: {
            variant: "h4",
            as: "h4",
            css: css`
                font-weight: 400 !important;
                margin-top: 0 !important;
                @media print {
                    font-size: 12px;
                }
            `,
        },
    },
    informationGridItem: {
        width: 12,
    },
    invoiceInformationGridItems: {
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    invoiceDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    termsGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    salespersonGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    shippingMethodGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    dueDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    vatNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    invoiceNotesGridItem: {
        width: 12,
    },
    SalesOrderGridItem: {
        width: [6, 6, 6, 3, 3],
        css: css`
            display: block;
            & > h3 {
                margin-bottom: 0.02rem !important;
            }
            @media print {
                padding: 5px !important;
            }
        `,
    },
    SalesOrderText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            margin-top: 0.2rem;
            @media print {
                font-size: ${TYPOGRAPHY.size.tiny};
                margin-top: 0 !important;
                a {
                    span {
                        color: ${COLORS.link.base} !important;
                    }
                }
            }
        `,
    },
    SalesOrderTitle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.bold};
            @media print {
                font-size: ${TYPOGRAPHY.size.tiny};
            }
        `,
    },
};

const styles = informationStyles;

const DiscrepancyDetailsInformation = ({
    language,
    discrepancyDetails,
    updateDiscrepancyStatus,
    loadDiscrepancyDetailsCollection,
    parameters,
    loadDiscrepancyCollection,
    featureWidgets,
    isSecurityRolesAssigned,
}: Props) => {
    const [loading, setIsLoading] = React.useState(false);
    const [selectedStatus, setSelectedStatus] = React.useState(discrepancyDetails?.discrepancyStatus || "");
    const toasterContext = React.useContext(ToasterContext);

    React.useEffect(() => {
        setSelectedStatus(discrepancyDetails?.discrepancyStatus || "");
    }, [discrepancyDetails?.discrepancyStatus]);

    if (!discrepancyDetails) {
        return null;
    }

    const handleChange = (event: React.FormEvent<HTMLSelectElement>) => {
        if (event.currentTarget.value) {
            setIsLoading(true);
            setSelectedStatus(event.currentTarget.value);
            updateDiscrepancyStatus({
                discrepancyId: discrepancyDetails.discrepancyNumber,
                discrepancyStatus: event.currentTarget.value,
                ...(event.currentTarget.value === "Complete" && { closedDate: new Date().toISOString() }),
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Discrepancy status updated successfully"),
                        messageType: "success",
                    });
                    loadDiscrepancyDetailsCollection({
                        discrepancyId: discrepancyDetails.discrepancyNumber,
                    });
                    setIsLoading(false);
                },
                onError: () => {
                    toasterContext.addToast({
                        body: translate("Failed to update discrepancy status"),
                        messageType: "danger",
                    });
                    setIsLoading(false);
                },
            });
        } else {
            setIsLoading(false);
        }
    };

    return (
        <GridContainer {...styles.invoiceInformationGridContainer} data-test-selector="invoiceDetail_information">
            <GridItem {...styles.informationGridItem}>
                <GridContainer {...styles.informationGridContainer}>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("PO Number")}
                            text={discrepancyDetails.poNumber || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Buyer Code")}
                            text={discrepancyDetails.buyerCode || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Buyer Region")}
                            text={discrepancyDetails.buyerRegion || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Buyer Email")}
                            text={discrepancyDetails.buyerEmail || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Creator")}
                            text={discrepancyDetails.creator || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Planner")}
                            text={discrepancyDetails.planner || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Responsibility")}
                            text={discrepancyDetails.responsibility || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Status")}
                            text={discrepancyDetails.status || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Fail Code")}
                            text={discrepancyDetails.failCode || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("CSR")}
                            text={discrepancyDetails.csr || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("DCC")}
                            text={discrepancyDetails.dcc || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("TCC")}
                            text={discrepancyDetails.tcc || ""}
                        />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Ship From")}
                            text={discrepancyDetails.shipFrom || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Vendor")}
                            text={discrepancyDetails.vendor || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("PLOC Ship To")}
                            text={discrepancyDetails.plocShipTo || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("PLOC Ship Name")}
                            text={discrepancyDetails.plocShipName || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Date Shipped")}
                            text={
                                discrepancyDetails.dateShipped
                                    ? getLocalizedDateTime({
                                          dateTime: new Date(discrepancyDetails.dateShipped),
                                          language,
                                      })
                                    : ""
                            }
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Date Received")}
                            text={
                                discrepancyDetails.dateReceived
                                    ? getLocalizedDateTime({
                                          dateTime: new Date(discrepancyDetails.dateReceived),
                                          language,
                                      })
                                    : ""
                            }
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Date Created")}
                            text={
                                discrepancyDetails.dateCreated
                                    ? getLocalizedDateTime({
                                          dateTime: new Date(discrepancyDetails.dateCreated),
                                          language,
                                      })
                                    : ""
                            }
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Carrier")}
                            text={discrepancyDetails.carrier || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Tracking Number")}
                            text={discrepancyDetails.trackingNumber || ""}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Date Closed")}
                            text={
                                discrepancyDetails.dateClosed
                                    ? getLocalizedDateTime({
                                          dateTime: new Date(discrepancyDetails.dateClosed),
                                          language,
                                      })
                                    : ""
                            }
                        />
                    </GridItem>
                    {featureWidgets?.includes("Discrepancy Status") ? null : (
                        <GridItem
                            {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}
                            style={{ flexDirection: "column" }}
                        >
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Discrepancy Status")}
                                text={""}
                            />
                            <Select
                                value={selectedStatus}
                                onChange={handleChange}
                                iconProps={{
                                    src: loading ? LoadingSpinner : ChevronDown,
                                    size: 20,
                                }}
                            >
                                <option value="">{translate("Select")}</option>
                                <option value="Open">{translate("Open")}</option>
                                <option value="In Progress">{translate("In Progress")}</option>
                                <option value="Pending">{translate("Pending")}</option>
                                <option value="Complete">{translate("Complete")}</option>
                            </Select>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DiscrepancyDetailsInformation),
    definition: {
        group: "Common",
        displayName: "Discrepancy Details Information",
    },
};

export default widgetModule;
